import { Component, Inject, OnInit } from "@angular/core";
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
} from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Subscription } from "rxjs";
import {
  Contact,
  Auction,
  AuctionTranslation,
  AuctionWithRelations,
  ContactWithRelations,
} from "src/app/api/models";
import {
  AvailableLocale,
  AVAILABLE_LOCALES,
} from "src/app/_static-data/available-locale.library";
import { NewAuction } from "src/app/api/models";

import {
  CountriesService,
  SimpleCountry,
} from "src/app/_services/countries.service";
import { TinyService } from "src/app/shared/services/tiny.service";

export interface AuctionCreateUpdateDialogData {
  context: "editor" | "inventory" | "item";
  auction?: AuctionWithRelations;
  contacts: ContactWithRelations[];
}

@Component({
  selector: "app-auction-create-update-dialog",
  templateUrl: "./auction-create-update-dialog.component.html",
  styleUrls: ["./auction-create-update-dialog.component.scss"],
  standalone: false,
})
export class AuctionCreateUpdateDialogComponent implements OnInit {
  availableLocales: AvailableLocale[] = AVAILABLE_LOCALES;
  TITLE_CONTROL_NAMES: string[] = ["originalTitle", "titleCS", "titleEN"];

  countries: SimpleCountry[] = [];

  form: UntypedFormGroup;
  mode: "create" | "update" = "create";
  context: "editor" | "inventory" | "item";
  auction: AuctionWithRelations;

  contacts: Contact[] = [];

  originalTitleLocaleSubscription: Subscription = new Subscription();
  titleEnSubscription: Subscription = new Subscription();
  titleCsSubscription: Subscription = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA) public defaults: AuctionCreateUpdateDialogData,
    private dialogRef: MatDialogRef<AuctionCreateUpdateDialogComponent>,
    private fb: UntypedFormBuilder,
    private countriesService: CountriesService,
    public tinyService: TinyService,
  ) {}

  ngOnDestroy(): void {
    this.originalTitleLocaleSubscription.unsubscribe();
    this.titleEnSubscription.unsubscribe();
    this.titleCsSubscription.unsubscribe();
  }

  async ngOnInit(): Promise<void> {
    this.countries = this.countriesService.getSimpleCountryList();
    this.context = this.defaults.context;
    this.contacts = this.defaults.contacts;
    if (this.defaults.auction) {
      this.mode = "update";
      this.auction = this.defaults.auction;
    } else {
      this.auction = {} as AuctionWithRelations;
    }
    this.prepareForm();
    if (!this.canEdit()) {
      this.form.disable({ emitEvent: false });
    }
  }

  /** Default submit function */
  save() {
    if (this.mode === "create") {
      this.create();
    } else if (this.mode === "update") {
      this.update();
    }
  }

  /** CRUD Functions */
  private create() {
    const ne: NewAuction = {
      auction: {
        originalTitle: this.getLocalizedOriginalTitle(),
        organizerId: this.form.value["organizerId"],
        year: this.form.value["year"],
        countryCode: this.form.value["countryCode"],
        curated: this.form.value["curated"],
      },
      translations: [
        {
          localeId: 1,
          title: this.form.value["titleCS"],
          isOriginalTitle:
            this.form.value["originalTitleLocale"] === "cs" ? true : false,
          cityName: this.form.value["cityNameCS"],
          description: this.form.value["descriptionCS"],
          year: this.form.value["yearCS"],
        },
        {
          localeId: 2,
          title: this.form.value["titleEN"],
          isOriginalTitle:
            this.form.value["originalTitleLocale"] === "en" ? true : false,
          cityName: this.form.value["cityNameEN"],
          description: this.form.value["descriptionEN"],
          year: this.form.value["yearEN"],
        },
      ],
    };
    this.dialogRef.close(ne);
  }

  private update() {
    const tCS = this.auction.translations?.find((a) => a.localeId === 1);
    const tEN = this.auction.translations?.find((a) => a.localeId === 2);

    //FIXME:Make OriginalTitle nor required
    const auction: Partial<Auction> = {
      id: this.auction.id,
      originalTitle: this.getLocalizedOriginalTitle(),
      organizerId: this.form.value["organizerId"],
      year: this.form.value["year"],
      countryCode: this.form.value["countryCode"],
      curated: this.form.value["curated"],
    };

    const translationCS: AuctionTranslation = {
      id: tCS.id,
      localeId: 1,
      title: this.form.value["titleCS"],
      cityName: this.form.value["cityNameCS"],
      description: this.form.value["descriptionCS"],
      year: this.form.value["yearCS"],
    };

    //Updates info about original title locale if was changed
    if (
      this.form.value["originalTitleLocale"] !== "cs" &&
      tCS.isOriginalTitle === true
    ) {
      translationCS.isOriginalTitle = false;
    } else if (
      this.form.value["originalTitleLocale"] === "cs" &&
      tCS.isOriginalTitle === false
    ) {
      translationCS.isOriginalTitle = true;
    }

    const translationEN: AuctionTranslation = {
      id: tEN.id,
      localeId: 2,
      title: this.form.value["titleEN"],
      cityName: this.form.value["cityNameEN"],
      description: this.form.value["descriptionEN"],
      year: this.form.value["yearEN"],
    };

    //Updates info about original title locale if was changed
    if (
      this.form.value["originalTitleLocale"] !== "en" &&
      tEN.isOriginalTitle === true
    ) {
      translationEN.isOriginalTitle = false;
    } else if (
      this.form.value["originalTitleLocale"] === "en" &&
      tEN.isOriginalTitle === false
    ) {
      translationEN.isOriginalTitle = true;
    }

    const translations: AuctionTranslation[] = [translationCS, translationEN];
    this.dialogRef.close({ auction, translations });
  }

  /* ---------------------------------------------------------
  DIALOG CONTENT METHODS
  --------------------------------------------------------- */
  isCreateMode() {
    return this.mode === "create";
  }

  isUpdateMode() {
    return this.mode === "update";
  }

  isEditorContext() {
    return this.context === "editor";
  }

  isInventoryContext() {
    return this.context === "inventory";
  }

  canEdit() {
    const isLocalAuction = this.defaults.auction?.inventoryId;
    return (
      this.isEditorContext() ||
      (this.isUpdateMode() && isLocalAuction && this.isInventoryContext()) ||
      this.isCreateMode()
    );
  }

  private prepareForm() {
    const translationCS = this.auction.translations?.find(
      (a) => a.localeId === 1,
    );
    const translationEN = this.auction.translations?.find(
      (a) => a.localeId === 2,
    );

    let originalTitleLocale: "other" | "cs" | "en" = "cs";

    if (translationCS?.isOriginalTitle === true) {
      originalTitleLocale = "cs";
    } else if (translationEN?.isOriginalTitle === true) {
      originalTitleLocale = "en";
    } else if (!translationCS && !translationEN) {
      originalTitleLocale = "cs";
    } else {
      originalTitleLocale = "other";
    }

    this.form = this.fb.group({
      originalTitle: [this.auction.originalTitle],
      originalTitleLocale: [originalTitleLocale],
      countryCode: [this.auction.countryCode],
      titleCS: [translationCS?.title || ""],
      titleEN: [translationEN?.title || ""],
      cityNameCS: [translationCS?.cityName || ""],
      cityNameEN: [translationEN?.cityName || ""],
      descriptionCS: [translationCS?.description || ""],
      descriptionEN: [translationEN?.description || ""],
      yearCS: [translationCS?.year || ""],
      yearEN: [translationEN?.year || ""],
      year: [this.auction.year],
      organizerId: [
        this.auction.organizerId,
        { validators: Validators.required },
      ],
      curated: [this.auction.curated || false],
    });

    this.toggleDisabledOriginalTitle(
      this.form.get("originalTitleLocale").value,
    );

    this.addModeBasedControls();
    this.subscribeToOriginalTitleLocale();
    this.subscribeToTitles();
  }

  private addModeBasedControls() {
    if (this.isCreateMode()) {
    }
    if (this.isUpdateMode()) {
    }
  }

  private subscribeToOriginalTitleLocale() {
    this.originalTitleLocaleSubscription = this.form
      .get("originalTitleLocale")
      .valueChanges.subscribe((value) => {
        this.toggleDisabledOriginalTitle(value);
      });
  }

  private toggleDisabledOriginalTitle(originalTitleLocaleValue: string) {
    if (originalTitleLocaleValue === "other") {
      this.form.get("originalTitle").enable();
    } else {
      this.form.get("originalTitle").disable();
    }
    this.setOriginalTitle();
    this.toggleRequiredTitle(originalTitleLocaleValue);
  }

  private subscribeToTitles() {
    this.titleEnSubscription = this.form
      .get("titleEN")
      .valueChanges.subscribe(() => {
        this.setOriginalTitle();
      });
    this.titleCsSubscription = this.form
      .get("titleCS")
      .valueChanges.subscribe(() => {
        this.setOriginalTitle();
      });
  }

  private setOriginalTitle() {
    switch (this.form.get("originalTitleLocale").value) {
      case "cs":
        this.form.get("originalTitle").setValue(this.form.get("titleCS").value);
        break;
      case "en":
        this.form.get("originalTitle").setValue(this.form.get("titleEN").value);
        break;
      case "other":
        this.form
          .get("originalTitle")
          .setValue(this.auction.originalTitle || "");
        break;
      default:
        break;
    }
  }

  private toggleRequiredTitle(originalTitleLocaleValue: string) {
    this.TITLE_CONTROL_NAMES.map((title) =>
      this.form.get(title).clearValidators(),
    );
    switch (originalTitleLocaleValue) {
      case "cs":
        this.form.get("titleCS").setValidators(Validators.required);

        break;
      case "en":
        this.form.get("titleEN").setValidators(Validators.required);

        break;
      case "other":
        this.form.get("originalTitle").setValidators(Validators.required);

        break;
      default:
        break;
    }

    this.TITLE_CONTROL_NAMES.map((title) =>
      this.form.get(title).updateValueAndValidity(),
    );
  }

  private getLocalizedOriginalTitle(): string {
    let originalTitle = this.form.value["originalTitle"];
    switch (this.form.value["originalTitleLocale"]) {
      case "cs":
        originalTitle = this.form.value["titleCS"];
        break;
      case "en":
        originalTitle = this.form.value["titleEN"];
        break;
      default:
        break;
    }
    return originalTitle;
  }

  getLocaleByCode(code: string): AvailableLocale {
    return this.availableLocales.find((locale) => locale.code === code);
  }
}
